import { Alert, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Content } from '@/core/components';

import { formatTravelNumber } from '../TravelAnalyticsUtils';
import { getTravelAggregatesOptions } from '../model/TravelRegistryQuery';
import { TravelReportingParams } from '../model/TravelRegistryTypes';
import { TravelCountryMapPanel } from './TravelCountryMapPanel';

interface TravelAggregatesPanelProps {
  params: TravelReportingParams;
}

interface StatProps {
  label: string;
  value: ReactNode;
}

function Stat({ label, value }: StatProps) {
  return (
    <Stack gap="2px">
      <Text
        size="xs"
        c="dimmed"
      >
        {label}
      </Text>
      <Text
        size="lg"
        fw="500"
      >
        {value}
      </Text>
    </Stack>
  );
}

function formatTravelEur(amount: number) {
  return `${formatTravelNumber(amount)} EUR`;
}

export function TravelAggregatesPanel({ params }: TravelAggregatesPanelProps) {
  const { t } = useTranslation();
  const { data, isError, isPending } = useQuery(getTravelAggregatesOptions(params));

  if (isError) {
    return <Alert color="red">{t('travel.registry.aggregates.error')}</Alert>;
  }
  if (isPending || data == null) {
    return (
      <Text
        size="sm"
        c="dimmed"
      >
        {t('travel.registry.aggregates.loading')}
      </Text>
    );
  }

  const {
    registrationYear,
    professorsTraveled,
    trips,
    financedDays,
    universityFinancedEur,
    byFundingSource,
    byCountry,
  } = data;
  const { erasmus } = data;

  return (
    <Content
      bg="white"
      title={`${t('travel.registry.aggregates.title')} ${registrationYear}`}
    >
      <Stack>
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <Stat
            label={t('travel.registry.aggregates.professorsTraveled')}
            value={formatTravelNumber(professorsTraveled)}
          />
          <Stat
            label={t('travel.registry.aggregates.trips')}
            value={formatTravelNumber(trips)}
          />
          <Stat
            label={t('travel.registry.aggregates.financedDays')}
            value={formatTravelNumber(financedDays)}
          />
          <Stat
            label={t('travel.registry.aggregates.universityFinanced')}
            value={universityFinancedEur == null ? '-' : formatTravelEur(universityFinancedEur)}
          />
        </SimpleGrid>

        {byFundingSource.length > 0 && (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('travel.registry.aggregates.fundingSource')}</Table.Th>
                <Table.Th>{t('travel.registry.aggregates.amount')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {byFundingSource.map(row => (
                <Table.Tr key={row.fundingSource}>
                  <Table.Td>{t(`travel.fundingSourceEnum.${row.fundingSource}`)}</Table.Td>
                  <Table.Td>{formatTravelEur(row.amountEur)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        {byCountry.length > 0 && <TravelCountryMapPanel byCountry={byCountry} />}

        {byCountry.length > 0 && (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('travel.registry.aggregates.country')}</Table.Th>
                <Table.Th>{t('travel.registry.aggregates.trips')}</Table.Th>
                <Table.Th>{t('travel.registry.aggregates.amount')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {byCountry.map(row => (
                <Table.Tr key={row.country}>
                  <Table.Td>{row.country === '' ? '-' : row.country}</Table.Td>
                  <Table.Td>{formatTravelNumber(row.trips)}</Table.Td>
                  <Table.Td>{formatTravelEur(row.amountEur)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        <Stack gap="xs">
          <Text
            size="sm"
            fw="500"
          >
            {t('travel.registry.aggregates.erasmus')}
          </Text>
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <Stat
              label={t('travel.registry.aggregates.trips')}
              value={formatTravelNumber(erasmus.trips)}
            />
            <Stat
              label={t('travel.registry.aggregates.requesters')}
              value={formatTravelNumber(erasmus.requesters)}
            />
            <Stat
              label={t('travel.registry.aggregates.amount')}
              value={erasmus.amountEur == null ? '-' : formatTravelEur(erasmus.amountEur)}
            />
            {erasmus.byType.map(row => (
              <Stat
                key={row.erasmusType}
                label={t(`travel.erasmusTypeEnum.${row.erasmusType}`)}
                value={`${formatTravelNumber(row.trips)} / ${formatTravelNumber(row.requesters)}`}
              />
            ))}
          </SimpleGrid>
        </Stack>
      </Stack>
    </Content>
  );
}
